/* global SpeechSDK */

class AzureSpeechService {
  constructor() {
    this.subscriptionKey = null;
    this.region = null;
    this.isInitialized = false;
    this.speechSynthesizer = null;
    this.speechRecognizer = null;
    this._isCurrentlyPlaying = false;
    this._lastTextToRead = null;
    this._lastRecognizedText = null;
    this._lastPronunciationResults = null;
    this._audioElement = null;
    this._currentAudioUrl = null;
    this._currentAudioBlob = null;
    
    // Callback functions for real-time updates
    this.onRecognizing = null;
    this.onRecognized = null;
    this.onCanceled = null;
    this.onSessionStopped = null;
  }

  // Initialize the service with Azure credentials
  async initialize(subscriptionKey, region) {
    try {
      console.log('🎯 Initializing Azure Speech Service...');
      
      if (!subscriptionKey || !region) {
        throw new Error('Azure credentials not provided');
      }

      this.subscriptionKey = subscriptionKey;
      this.region = region;
      this.isInitialized = true;
      
      console.log('✅ Azure Speech Service initialized successfully');
      
    } catch (error) {
      console.error('❌ Error initializing Azure Speech Service:', error);
      throw error;
    }
  }

  // Check if service is ready
  isReady() {
    return this.isInitialized && this.subscriptionKey && this.region;
  }

  // Set playing state
  setPlayingState(isPlaying) {
    console.log('🎮 setPlayingState called:', {
      oldState: this._isCurrentlyPlaying,
      newState: isPlaying
    });
    this._isCurrentlyPlaying = isPlaying;
  }

  // Check if speech is currently playing
  isPlaying() {
    const playing = this.isInitialized && this.speechSynthesizer !== null && this._isCurrentlyPlaying;
    console.log('🎵 isPlaying() check:', {
      isInitialized: this.isInitialized,
      hasSynthesizer: this.speechSynthesizer !== null,
      _isCurrentlyPlaying: this._isCurrentlyPlaying,
      result: playing
    });
    return playing;
  }

  // Start model reading (TTS)
  async startModelReading(text) {
    try {
      console.log('🎯 Starting Azure TTS with browser audio control...');
      console.log('Text to read:', text.substring(0, 100) + '...');
      
      if (!this.isReady()) {
        throw new Error('Azure Speech Service not ready');
      }

      // Simple cleanup before starting new TTS
      if (this.speechSynthesizer) {
        try {
          // Check if synthesizer is already disposed
          if (!this.speechSynthesizer.isDisposed) {
            this.speechSynthesizer.close();
            console.log('✅ Previous synthesizer closed');
          } else {
            console.log('ℹ️ Previous synthesizer already disposed');
          }
        } catch (e) {
          console.log('⚠️ Close error:', e);
        }
        // Always set to null to force recreation
        this.speechSynthesizer = null;
      }
      
      // Recreate synthesizer for new TTS (with event listeners)
      const speechConfig = SpeechSDK.SpeechConfig.fromSubscription(this.subscriptionKey, this.region);
      speechConfig.speechSynthesisLanguage = "en-US";
      speechConfig.speechSynthesisVoiceName = "en-US-JennyNeural";
      this.speechSynthesizer = new SpeechSDK.SpeechSynthesizer(speechConfig);
      
      // Add event listeners for new synthesizer
      this.speechSynthesizer.synthesisStarted = () => {
        console.log('🎤 Azure TTS started - audio playing through browser');
        this.setPlayingState(true);
      };
      
      this.speechSynthesizer.synthesisCompleted = (result) => {
        console.log('✅ Azure TTS completed - audio finished');
        this.setPlayingState(false);
      };
      
      this.speechSynthesizer.synthesisCanceled = (result) => {
        console.log('🛑 Azure TTS canceled - audio stopped');
        this.setPlayingState(false);
      };
      
      console.log('✅ New synthesizer created with browser control');
      
      // Start Azure TTS - it will play audio directly through browser
      console.log('🎵 Starting Azure TTS playback...');
      this._lastTextToRead = text; // Store text for resume
      const result = await this.speechSynthesizer.speakTextAsync(text);
      
      console.log('✅ Azure TTS started successfully');
      console.log('Audio is now playing through browser - use our buttons to control it!');
      
      return 'browser-controlled';
      
    } catch (error) {
      console.error('❌ Error starting Azure TTS:', error);
      throw error;
    }
  }

  // Stop model reading
  async stopModelReading() {
    try {
      console.log('🛑 Stop button clicked!');
      
      if (this.speechSynthesizer && this.speechSynthesizer.isReady()) {
        console.log('🎯 Azure service ready, stopping audio...');
        this.pauseBrowserAudio();
        console.log('✅ Audio stop command sent');
      } else {
        console.log('⚠️ Azure service not ready');
      }
      
      console.log('✅ Model reading stopped');
      
    } catch (error) {
      console.error('❌ Error stopping model reading:', error);
    }
  }

  // Pause browser audio
  async pauseBrowserAudio() {
    try {
      console.log('⏸️ Pausing Azure TTS audio...');
      
      // Method 1: IMMEDIATELY stop Azure TTS synthesis
      if (this.speechSynthesizer) {
        try {
          this.speechSynthesizer.close();
          console.log('✅ Azure TTS synthesis stopped');
        } catch (e) {
          console.log('⚠️ Azure TTS stop error:', e);
        }
        this.speechSynthesizer = null;
      }
      
      // Method 2: Force stop ALL browser audio contexts
      const audioContexts = [];
      for (let i = 0; i < 10; i++) {
        try {
          const context = new (window.AudioContext || window.webkitAudioContext)();
          audioContexts.push(context);
        } catch (e) {
          break;
        }
      }
      
      // Suspend all audio contexts
      for (let i = 0; i < audioContexts.length; i++) {
        try {
          await audioContexts[i].suspend();
          console.log(`✅ Audio context ${i} suspended`);
        } catch (e) {
          console.log(`⚠️ Audio context ${i} suspend error:`, e);
        }
      }
      
      // Close all audio contexts
      for (let i = 0; i < audioContexts.length; i++) {
        try {
          await audioContexts[i].close();
          console.log(`✅ Audio context ${i} closed`);
        } catch (e) {
          console.log(`⚠️ Audio context ${i} close error:`, e);
        }
      }
      
      // Method 3: Force stop ALL media elements
      const allMediaElements = document.querySelectorAll('audio, video');
      for (let i = 0; i < allMediaElements.length; i++) {
        try {
          const element = allMediaElements[i];
          element.pause();
          element.currentTime = 0;
          element.src = '';
          console.log(`✅ Media element stopped and cleared`);
        } catch (e) {
          console.log(`⚠️ Media element stop error:`, e);
        }
      }
      
      // Method 4: Force stop any ongoing speech synthesis
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
        console.log('✅ Speech synthesis canceled');
      }
      
      // Method 5: Force stop any media streams
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          const tracks = stream.getTracks();
          for (let i = 0; i < tracks.length; i++) {
            tracks[i].stop();
            console.log(`✅ Media track stopped`);
          }
        } catch (e) {
          console.log('⚠️ Media stream stop error:', e);
        }
      }
      
      // Method 6: NEW - Force interrupt Azure TTS audio stream
      try {
        console.log('🔇 Creating silent audio to interrupt Azure TTS...');
        // Create a silent audio context to interrupt any playing audio
        const silentContext = new (window.AudioContext || window.webkitAudioContext)();
        const silentBuffer = silentContext.createBuffer(1, 44100, 44100);
        const silentSource = silentContext.createBufferSource();
        silentSource.buffer = silentBuffer;
        silentSource.connect(silentContext.destination);
        silentSource.start();
        silentSource.stop(0.1); // Stop after 0.1 seconds
        
        // Close the context after interrupting
        setTimeout(() => {
          try {
            silentContext.close();
            console.log('✅ Silent audio context closed');
          } catch (e) {
            console.log('⚠️ Silent context close error:', e);
          }
        }, 200);
        
        console.log('✅ Silent audio buffer created to interrupt Azure TTS');
      } catch (e) {
        console.log('⚠️ Silent audio buffer error:', e);
      }
      
      // Method 7: NEW - Force stop any Web Audio API sources
      try {
        console.log('🔇 Stopping all Web Audio sources...');
        // Get all audio contexts and force stop them
        const contexts = [];
        for (let i = 0; i < 3; i++) {
          try {
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            contexts.push(ctx);
          } catch (e) {
            break;
          }
        }
        
        // Force suspend and close all contexts
        contexts.forEach((ctx, index) => {
          try {
            if (ctx.state === 'running') {
              ctx.suspend();
              console.log(`✅ Web Audio context ${index} suspended`);
            }
            ctx.close();
            console.log(`✅ Web Audio context ${index} closed`);
          } catch (e) {
            console.log(`⚠️ Web Audio context ${index} error:`, e);
          }
        });
      } catch (e) {
        console.log('⚠️ Web Audio control error:', e);
      }
      
      this.setPlayingState(false);
      console.log('⏸️ Azure TTS audio paused successfully');
      
      // Method 8: FINAL - Force stop ALL audio by muting the entire page
      try {
        console.log('🔇 Applying final audio kill switch...');
        
        // Mute all audio elements
        const allAudio = document.querySelectorAll('audio');
        allAudio.forEach(audio => {
          audio.muted = true;
          audio.volume = 0;
          audio.pause();
          audio.currentTime = 0;
        });
        
        // Mute all video elements
        const allVideo = document.querySelectorAll('video');
        allVideo.forEach(video => {
          video.muted = true;
          video.volume = 0;
          video.pause();
          video.currentTime = 0;
        });
        
        // Set global audio volume to 0
        if (window.AudioContext || window.webkitAudioContext) {
          try {
            const killContext = new (window.AudioContext || window.webkitAudioContext)();
            const gainNode = killContext.createGain();
            gainNode.gain.value = 0;
            gainNode.connect(killContext.destination);
            console.log('✅ Global audio gain set to 0');
            setTimeout(() => killContext.close(), 100);
          } catch (e) {
            console.log('⚠️ Global audio kill error:', e);
          }
        }
        
        console.log('✅ Final audio kill switch applied');
      } catch (e) {
        console.log('⚠️ Final audio kill error:', e);
      }
      
    } catch (error) {
      console.error('❌ Error pausing Azure TTS audio:', error);
    }
  }

  // Resume browser audio
  resumeBrowserAudio() {
    try {
      console.log('▶️ Resuming Azure TTS audio...');
      
      // Check if we have text to resume
      if (!this._lastTextToRead) {
        console.log('⚠️ No text to resume - please start reading again');
              return;
            }
            
      // Method 1: Restore audio system after our aggressive pause
      try {
        console.log('🔊 Restoring audio system...');
        
        // Unmute all audio elements
        const allAudio = document.querySelectorAll('audio');
        allAudio.forEach(audio => {
          audio.muted = false;
          audio.volume = 1;
        });
        
        // Unmute all video elements
        const allVideo = document.querySelectorAll('video');
        allVideo.forEach(video => {
          video.muted = false;
          video.volume = 1;
        });
        
        console.log('✅ Audio system restored');
      } catch (e) {
        console.log('⚠️ Audio restore error:', e);
      }
      
      // Method 2: Always recreate synthesizer for resume (since pause closes it)
      console.log('🔄 Recreating synthesizer for resume...');
      const speechConfig = SpeechSDK.SpeechConfig.fromSubscription(this.subscriptionKey, this.region);
      speechConfig.speechSynthesisLanguage = "en-US";
      speechConfig.speechSynthesisVoiceName = "en-US-JennyNeural";
      
      this.speechSynthesizer = new SpeechSDK.SpeechSynthesizer(speechConfig);
      
      // Re-add event listeners
      this.speechSynthesizer.synthesisStarted = () => {
        console.log('🎤 Azure TTS resumed - audio playing through browser');
        this.setPlayingState(true);
      };
      
      this.speechSynthesizer.synthesisCompleted = (result) => {
        console.log('✅ Azure TTS completed - audio finished');
        this.setPlayingState(false);
      };
      
      this.speechSynthesizer.synthesisCanceled = (result) => {
        console.log('🛑 Azure TTS canceled - audio stopped');
        this.setPlayingState(false);
      };
      
      console.log('✅ Synthesizer recreated for resume');
      
      // Resume reading the last text
      console.log('🎵 Resuming reading from where we left off...');
      console.log('📝 Text to resume:', this._lastTextToRead.substring(0, 100) + '...');
      
      // Start the synthesis and wait for it
      this.speechSynthesizer.speakTextAsync(this._lastTextToRead).then(() => {
        console.log('✅ Resume synthesis started successfully');
      }).catch((error) => {
        console.error('❌ Resume synthesis failed:', error);
        this.setPlayingState(false);
      });
      
    } catch (error) {
      console.error('❌ Error resuming Azure TTS audio:', error);
      // If resume fails, reset state
      console.log('🔄 Resetting state after resume error...');
      this.speechSynthesizer = null;
      this._isCurrentlyPlaying = false;
    }
  }

  // Speech-to-Text with Pronunciation Assessment
  async startReadingSession() {
    try {
      console.log('🎤 Starting reading session with Azure STT...');
      
      // Check if service is ready
      if (!this.isInitialized) {
        throw new Error('Azure Speech Service not initialized');
      }

      // Create speech config for STT
      const speechConfig = SpeechSDK.SpeechConfig.fromSubscription(this.subscriptionKey, this.region);
      speechConfig.speechRecognitionLanguage = "en-US";
      
      // Configure Pronunciation Assessment
      speechConfig.setProperty(SpeechSDK.PropertyId.SpeechServiceConnection_EndSilenceTimeoutMs, "3000");
      speechConfig.setProperty(SpeechSDK.PropertyId.SpeechServiceConnection_InitialSilenceTimeoutMs, "2000");
      speechConfig.setProperty(SpeechSDK.PropertyId.SpeechServiceConnection_WordLevelTimestamps, "true");
      
      // Create audio config for microphone input
      const audioConfig = SpeechSDK.AudioConfig.fromDefaultMicrophoneInput();
      
      // Create recognizer with Pronunciation Assessment
      this.speechRecognizer = new SpeechSDK.SpeechRecognizer(speechConfig, audioConfig);
      
      // Add event listeners
      this.speechRecognizer.recognized = (s, e) => {
        if (e.result.reason === SpeechSDK.ResultReason.RecognizedSpeech) {
          console.log('✅ Speech recognized:', e.result.text);
          this._lastRecognizedText = e.result.text;
          
          // Parse pronunciation assessment results
          if (e.result.properties && e.result.properties.getProperty(SpeechSDK.PropertyId.SpeechServiceResponse_JsonResult)) {
            const pronunciationAssessment = JSON.parse(e.result.properties.getProperty(SpeechSDK.PropertyId.SpeechServiceResponse_JsonResult));
            console.log('📊 Pronunciation assessment:', pronunciationAssessment);
            this._lastPronunciationResults = pronunciationAssessment;
          }
          
          // Call callback if set
          if (this.onRecognized) {
            this.onRecognized(e.result.text);
          }
        }
      };
      
      this.speechRecognizer.recognizing = (s, e) => {
        console.log('🎤 Recognizing...', e.result.text);
        
        // Call callback if set
        if (this.onRecognizing) {
          this.onRecognizing(e.result.text);
        }
      };
      
      this.speechRecognizer.canceled = (s, e) => {
        console.log('❌ Recognition canceled:', e.reason);
        if (e.reason === SpeechSDK.CancellationReason.Error) {
          console.error('❌ Recognition error:', e.errorDetails);
        }
        
        // Call callback if set
        if (this.onCanceled) {
          this.onCanceled(e.reason, e.errorDetails);
        }
      };
      
      this.speechRecognizer.sessionStopped = (s, e) => {
        console.log('🛑 Recognition session stopped');
        
        // Call callback if set
        if (this.onSessionStopped) {
          this.onSessionStopped();
        }
      };
      
      // Start continuous recognition
      await this.speechRecognizer.startContinuousRecognitionAsync();
      console.log('✅ Reading session started successfully');
      
      return 'started';
      
    } catch (error) {
      console.error('❌ Error starting reading session:', error);
      throw error;
    }
  }

  // Stop reading session
  async stopReadingSession() {
    try {
      console.log('🛑 Stopping reading session...');
      
      if (this.speechRecognizer) {
        await this.speechRecognizer.stopContinuousRecognitionAsync();
        this.speechRecognizer.close();
        this.speechRecognizer = null;
        console.log('✅ Reading session stopped successfully');
      }
      
      return 'stopped';
      
    } catch (error) {
      console.error('❌ Error stopping reading session:', error);
      throw error;
    }
  }

  // Get last recognition results
  getLastRecognitionResults() {
    return {
      text: this._lastRecognizedText || '',
      pronunciation: this._lastPronunciationResults || null
    };
  }

  // Check if reading session is active
  isReadingSessionActive() {
    return this.speechRecognizer !== null;
  }

  // HTML5 Audio Control Methods
  pauseAudio() {
    if (this._audioElement) {
      this._audioElement.pause();
      this.setPlayingState(false);
      console.log('⏸️ Audio playback paused');
    }
  }

  resumeAudio() {
    if (this._audioElement) {
      this._audioElement.play();
      this.setPlayingState(true);
      console.log('▶️ Audio playback resumed');
    }
  }

  stopAudio() {
    // Stop HTML5 audio if available
    if (this._audioElement) {
      this._audioElement.pause();
      this._audioElement.currentTime = 0;
      this.setPlayingState(false);
      console.log('⏹️ HTML5 audio playback stopped');
    }
    
    // Stop fallback speech synthesis if active
    this.stopFallbackSpeech();
  }

  // Set audio element reference
  setAudioElement(audioElement) {
    this._audioElement = audioElement;
    
    // Add event listeners to track state
    if (audioElement) {
      audioElement.addEventListener('play', () => this.setPlayingState(true));
      audioElement.addEventListener('pause', () => this.setPlayingState(false));
      audioElement.addEventListener('ended', () => this.setPlayingState(false));
      audioElement.addEventListener('error', () => this.setPlayingState(false));
    }
  }

  // Clean up audio resources
  cleanupAudio() {
    if (this._currentAudioUrl) {
      URL.revokeObjectURL(this._currentAudioUrl);
      this._currentAudioUrl = null;
    }
    if (this._currentAudioBlob) {
      this._currentAudioBlob = null;
    }
    this.setPlayingState(false);
  }

  // Stop fallback speech synthesis
  stopFallbackSpeech() {
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
      this.setPlayingState(false);
      console.log('🛑 Fallback speech synthesis stopped');
    }
  }

  // Check if SpeechSDK is available globally
  static isSpeechSDKAvailable() {
    return typeof SpeechSDK !== 'undefined';
  }

  // Get available voices
  async getAvailableVoices() {
    if (!this.isInitialized) {
      throw new Error('Service not initialized');
    }

    try {
      const voices = await this.speechSynthesizer.getVoicesAsync();
      return voices.voices;
    } catch (error) {
      console.error('❌ Error getting voices:', error);
      return [];
    }
  }
}

// Export the service
export default AzureSpeechService;
